import { useCallback, useEffect, useMemo, useState } from 'react'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { TopHeader } from '../components/TopHeader.tsx'
import { WorkerStatusCard } from '../components/WorkerStatusCard.tsx'
import { Positions } from '../components/views/Positions.tsx'
import { MarketScan } from '../components/views/MarketScan.tsx'
import { PnLOverview } from '../components/views/PnLOverview.tsx'
import { WorkerControls } from '../components/views/WorkerControls.tsx'
import { LiveLogs } from '../components/views/LiveLogs.tsx'
import { fetchAutoExecuteStatus, logoutDashboard, getWsUrl } from '../lib/api.ts'
import { useWebSocket } from '../hooks/useWebSocket.ts'

export function Dashboard() {
  const [workerDrawerOpen, setWorkerDrawerOpen] = useState(false)
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false)
  const [workerRunning, setWorkerRunning] = useState(false)

  const wsUrl = getWsUrl()

  const { connected, stats: wsStats } = useWebSocket(wsUrl)

  const isRunning = useMemo(
    () => wsStats?.autoExecute?.running ?? workerRunning,
    [wsStats, workerRunning]
  )

  const lastScan = wsStats?.autoExecute?.lastScanAt ?? null
  const successfulMerges = wsStats?.autoExecute?.hedgesFound ?? 0
  const failedAttempts = wsStats?.autoExecute?.entriesFound ?? 0

  const onWorkerStatus = useCallback((running: boolean) => {
    setWorkerRunning(running)
  }, [])

  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const status = await fetchAutoExecuteStatus()
        if (!cancelled && status.success) {
          onWorkerStatus(status.running)
        }
      } catch {
        // ignore
      }
    }
    poll()
    const interval = setInterval(poll, 15000)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [onWorkerStatus])

  const handleLogout = async () => {
    try {
      await logoutDashboard()
      window.location.reload()
    } catch {
      window.location.reload()
    }
  }

  return (
    <div className="dashboard-layout">
      <TopHeader
        connected={connected}
        isRunning={isRunning}
        onOpenWorker={() => setWorkerDrawerOpen(true)}
        onOpenLogs={() => setLogsDrawerOpen(true)}
        onLogout={handleLogout}
      />

      <main className="main-area">
        <div className="one-page-content">
          <WorkerStatusCard
            running={isRunning}
            lastScan={lastScan}
            successfulMerges={successfulMerges}
            failedAttempts={failedAttempts}
            onClick={() => setWorkerDrawerOpen(true)}
          />
          <div className="flex flex-col gap-8 mt-8">
            <MarketScan />
            <WorkerControls />
            <Positions />
            <PnLOverview />
          </div>
        </div>
      </main>

      {/* Worker Controls drawer */}
      <Sheet open={workerDrawerOpen} onOpenChange={setWorkerDrawerOpen}>
        <SheetContent side="right" className="w-[600px] sm:max-w-[600px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle
              className="font-mono"
              style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              ⚙️ Worker Controls
            </SheetTitle>
          </SheetHeader>
          <WorkerControls />
        </SheetContent>
      </Sheet>

      {/* Live Logs drawer */}
      <Sheet open={logsDrawerOpen} onOpenChange={setLogsDrawerOpen}>
        <SheetContent
          side="right"
          className="w-[700px] sm:max-w-[700px]"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <SheetHeader>
            <SheetTitle
              className="font-mono"
              style={{ fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase' }}
            >
              📋 Live Logs
            </SheetTitle>
          </SheetHeader>
          <div style={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <LiveLogs />
          </div>
        </SheetContent>
      </Sheet>
    </div>
  )
}
